import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * Scrolls the window to the top whenever the route changes — with two
 * exceptions:
 *  1. **Hash navigation** (e.g. `/#how`) → scroll the target section into
 *     view. Used by the mega-nav "Features" / "Pricing" items so they work
 *     correctly from any route (not just the landing page).
 *  2. **Landing page (`/`)** without hash → remembers the user's last scroll
 *     position so coming back from `/courses`, `/resources`, etc. picks up
 *     exactly where they left off.
 *
 * Works consistently across:
 *  - Mobile browsers (touch scroll inertia)
 *  - Desktop (mouse wheel)
 *  - Installed PWA (where the body itself sometimes scrolls)
 */
const LANDING_KEY = "kpp-landing-scroll";

function scrollToHash(hash) {
  if (!hash) return;
  const id = hash.replace(/^#/, "");
  if (!id) return;
  // The destination section might not be mounted yet (e.g. arriving on the
  // landing page from another route). Retry a few times before giving up.
  let attempts = 0;
  const tick = () => {
    const el = document.getElementById(id);
    if (el) {
      try { el.scrollIntoView({ behavior: "auto", block: "start" }); }
      catch {
        const top = el.getBoundingClientRect().top + (window.scrollY || 0);
        window.scrollTo(0, top);
      }
      return;
    }
    attempts += 1;
    if (attempts < 15) setTimeout(tick, 80);
  };
  tick();
}

export default function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const prevPath = useRef(pathname);

  // Capture the landing-page scroll position right before we navigate away.
  // We only save when there's no active hash so an in-page anchor click
  // doesn't overwrite the user's actual reading position.
  useEffect(() => {
    const onScroll = () => {
      if (window.location.pathname === "/" && !window.location.hash) {
        sessionStorage.setItem(LANDING_KEY, String(window.scrollY || 0));
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const wasLanding = prevPath.current === "/";
    const isLanding = pathname === "/";
    prevPath.current = pathname;

    // Save final position just before leaving the landing page
    if (wasLanding && !isLanding) {
      sessionStorage.setItem(LANDING_KEY, String(window.scrollY || 0));
    }

    const id = window.requestAnimationFrame(() => {
      // Hash navigation always wins — bring the section into view.
      if (hash) { scrollToHash(hash); return; }

      // Returning to the landing page → restore prior scroll (or top if none)
      if (isLanding) {
        const saved = parseInt(sessionStorage.getItem(LANDING_KEY) || "0", 10);
        if (Number.isFinite(saved) && saved > 0) {
          try { window.scrollTo({ top: saved, left: 0, behavior: "auto" }); }
          catch { window.scrollTo(0, saved); }
          return;
        }
      }
      // Every other route → scroll to top
      try { window.scrollTo({ top: 0, left: 0, behavior: "auto" }); }
      catch { window.scrollTo(0, 0); }
      if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
      if (document.documentElement) document.documentElement.scrollTop = 0;
      if (document.body) document.body.scrollTop = 0;
    });
    return () => window.cancelAnimationFrame(id);
  }, [pathname, hash]);

  return null;
}
